<script setup lang="ts">
/**
 * One catalogue item, opened from its card, at inspection size.
 *
 * The grid answers *which of these*; this answers *this one, then*. A card is a
 * tile in a shelf of twenty — a fixed still, a blurb clipped to two lines, one
 * number per stat — and none of that is wrong, it is simply the wrong size for
 * the last decision before money moves. So the workbench is the same facts with
 * the room to read them: the model live and turnable rather than photographed
 * from one bearing, the stats compared against what is fitted, and every control
 * the card carries gathered in the column beside it.
 *
 * ## It decides nothing
 *
 * Every action is re-emitted for `GarageScreen` to handle, exactly as `ItemCard`
 * does, and for the same reason: the confirmation dialog and the store call live
 * in one place per purchase rather than one place per surface. A second window
 * that asked its own question would be a second wording to drift, and — worse — a
 * second place a rule could appear to be enforced. The server re-validates every
 * one of these; a disabled button here is a courtesy, never the enforcement.
 *
 * The gate logic is deliberately duplicated *as computeds* rather than passed in,
 * because it is derived from the item and nothing else: `locked` is owned against
 * either gate, since a gate is permission to buy and never permission to keep.
 * If a third surface ever needs it, that is the moment to lift it into a helper
 * beside `stat-display.ts` — not before.
 *
 * ## There is no header any more, and the price moved
 *
 * The name and the price used to sit in a bordered bar above everything, and the
 * price only appeared once — beside the name, in a state (locked, unaffordable)
 * the buy button below it did not repeat. That put the one figure a buyer needs
 * most somewhere their eye is not, at the moment they are deciding whether to
 * press the button.
 *
 * So the price rides on the buy button itself, in every state it can be in —
 * *Locked · 900 cr*, *Not enough credits · 900 cr*, *Buy · 900 cr* — because a
 * price a player cannot act on yet is still the number they are working towards.
 * With it there, the header had nothing left that was not better placed
 * elsewhere: the name and tier moved to a translucent title bar over the top of
 * the viewer and the detail column (see `.title-bar`), and the close control
 * became a floating chip in the panel's own corner rather than a row of its own.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import type { GarageItem } from '@/net/api';
import StatList from './StatList.vue';
import CurrencyIcon from '@/ui/CurrencyIcon.vue';
import UpgradeInstallButton from './UpgradeInstallButton.vue';
import { HULL_STATS, TURRET_STATS, formatCredits, formatStat } from './stat-display';
import type { WorkbenchScene, WorkbenchSubject } from './workbench-scene';
import { previewsAllowed } from '@/state/settings';

const props = defineProps<{
  item: GarageItem;
  /** The equivalent part currently fitted, for the stat comparison. */
  current: GarageItem | null;
  /** The other half of the loadout, so the model is built from a complete pair. */
  counterpartId: string | null;
  credits: number;
  diamonds: number;
  busy: boolean;
}>();

const emit = defineEmits<{
  close: [];
  purchase: [id: string];
  upgrade: [id: string];
  /** Install a finished upgrade. */
  installUpgrade: [id: string];
  /** Finish a running install now, for diamonds. */
  skipUpgrade: [id: string];
  prestige: [id: string];
  equip: [id: string];
  customise: [id: string];
  showResearch: [nodeId: string];
}>();

const isHull = computed(() => props.item.kind === 'HULL');
const kindLabel = computed(() => (isHull.value ? 'Hull' : 'Turret'));
const descriptors = computed(() => (isHull.value ? HULL_STATS : TURRET_STATS));

/** Compare against the fitted part, unless this *is* the fitted part. */
const reference = computed(() =>
  props.current && props.current.id !== props.item.id ? props.current.currentStats : null,
);

// ─── The same two gates the card reports, and the same rule ──────────────────

const researchLocked = computed(() => !props.item.owned && !props.item.researchSatisfied);
const rankLocked = computed(() => !props.item.owned && !props.item.rankSatisfied);
const locked = computed(() => researchLocked.value || rankLocked.value);

const isPremium = computed(() => props.item.diamondPrice !== null);
const awardedAt = computed(() => props.item.awardedAtRank);
const forSale = computed(() => !props.item.retired && awardedAt.value === null);

/**
 * The price, split into a figure and a currency so the unit can be a drawn mark.
 *
 * It used to be one string — `` `${diamondPrice} 💎` `` or `` `${price} cr` `` —
 * and a baked-in unit cannot become a component. So the figure and the kind are
 * separate now, and the two non-numeric states keep a word: an item that is not
 * for sale has no figure for a mark to sit beside, and *No longer sold* is the
 * whole answer to why there is no price. Neither is reachable from the buy
 * button, which only ever renders `v-if="forSale"` — the guaranteed-numeric case
 * is what lets that button always print `priceAmount` without a null check.
 */
const priceAmount = computed(() =>
  props.item.retired || awardedAt.value !== null
    ? null
    : isPremium.value
      ? String(props.item.diamondPrice)
      : formatCredits(props.item.price),
);
const priceKind = computed<'credits' | 'diamonds'>(() =>
  isPremium.value ? 'diamonds' : 'credits',
);

const canBuy = computed(
  () =>
    forSale.value &&
    !props.item.owned &&
    props.item.researchSatisfied &&
    props.item.rankSatisfied &&
    (isPremium.value
      ? props.diamonds >= (props.item.diamondPrice ?? 0)
      : props.credits >= props.item.price),
);
const canUpgrade = computed(
  () =>
    props.item.owned &&
    props.item.nextUpgradeCost !== null &&
    props.credits >= props.item.nextUpgradeCost,
);
const maxed = computed(
  () =>
    props.item.owned &&
    props.item.maxUpgradeLevel > 0 &&
    props.item.nextUpgradeCost === null &&
    props.item.upgrade === null,
);
const showsPrestige = computed(() => props.item.owned && props.item.maxPrestigeLevel > 0);
const prestigeMaxed = computed(
  () => props.item.prestigeUnlocked && props.item.nextPrestigeCost === null,
);
const canPrestige = computed(
  () =>
    props.item.prestigeUnlocked &&
    props.item.nextPrestigeCost !== null &&
    props.diamonds >= props.item.nextPrestigeCost,
);
const canCustomise = computed(() => isHull.value && props.item.owned);

const gateNodeId = computed(() => props.item.requiredResearchId);
const gateLabel = computed(() => props.item.requiredResearchName ?? 'further research');

/**
 * What the next upgrade level actually buys, as one short phrase on the button.
 *
 * The card can only say what a level *costs*; with the room for it, saying what
 * it *does* is the more useful half — an upgrade is the one purchase in the
 * garage whose effect is invisible until it is bought. It is the largest
 * improving delta, from the server's own `nextStats`, and it is **never computed
 * from a curve here**: the upgrade ladder lives in `stat-resolution.ts`, and a
 * browser working it out itself would be free to promise a figure the purchase
 * does not deliver. Null when there is no next level, or when the server did not
 * send the figures — in which case the button simply says the price, as the card
 * has always done.
 */
const upgradeGain = computed(() => {
  const next = props.item.nextStats;
  if (!next) return null;
  let best: { text: string; amount: number } | null = null;
  for (const d of descriptors.value) {
    const from = props.item.currentStats[d.key];
    const to = next[d.key];
    if (typeof from !== 'number' || typeof to !== 'number' || to === from) continue;
    // `lowerIsBetter` flips which direction counts as a gain — a reload that
    // drops by 0.3 s is an improvement, and a naive `to - from` would report it
    // as a loss and pick some other stat to advertise.
    const gain = d.lowerIsBetter ? from - to : to - from;
    if (gain <= 0) continue;
    // Ranked as a fraction of the bar's own full scale, so stats measured in
    // wildly different units are comparable: +15 hull integrity and +0.5 rad/s
    // of traverse are not otherwise on the same axis, and the raw numbers would
    // always hand the headline to whichever stat happens to be counted in
    // hundreds.
    const weight = gain / d.full;
    if (!best || weight > best.amount) {
      best = { text: `+${formatStat(d, Math.abs(to - from))}`, amount: weight };
    }
  }
  return best?.text ?? null;
});

// ─── The live viewer ─────────────────────────────────────────────────────────

/**
 * The panel itself, focused on mount.
 *
 * Focus lands in the dialog so Escape and Tab go somewhere sensible rather than
 * into the shop grid behind the scrim — `RankLadder`'s and `BattlePicker`'s
 * convention. Deliberately the panel and not the canvas: the canvas is taken
 * *out* of the tab order by the scene (Babylon makes it focusable so a game can
 * read keys from it), because the useful focus stops here are the buttons.
 */
const dialog = ref<HTMLElement | null>(null);
const canvas = ref<HTMLCanvasElement | null>(null);
const modelsOff = computed(() => !previewsAllowed());
/** False until the meshes are in, so the panel can say it is still loading. */
const loaded = ref(false);
const zoomed = ref(false);
/** Set once a drag has happened, which is what retires the hint. */
const turned = ref(false);

let scene: WorkbenchScene | null = null;

/** Which half to draw. The catalogue's own rule — see `showOnly`. */
const subject = computed<WorkbenchSubject>(() => (isHull.value ? 'hull' : 'turret'));

const viewerHull = computed(() => (isHull.value ? props.item.id : props.counterpartId));
const viewerTurret = computed(() => (isHull.value ? props.counterpartId : props.item.id));

onMounted(async () => {
  // Before the await, so Escape closes the window while the renderer is still
  // being fetched — and so it works at all when previews are switched off, which
  // returns below.
  dialog.value?.focus();

  if (modelsOff.value || !canvas.value) return;
  // Imported here rather than at the top of the file so Babylon is not pulled
  // into the garage's critical path — the same reason `ItemPreview` defers it.
  // The dialog's text, stats and buttons paint first and the model arrives into
  // them, which also means a player who never opens a workbench never fetches a
  // renderer.
  const { WorkbenchScene: Scene } = await import('./workbench-scene');
  if (!canvas.value) return;
  scene = new Scene(canvas.value);
  scene.start();
  // No livery is ever shown here — see the header for why the toggle that used
  // to offer one is gone. `null` is the neutral showroom finish `setPair` falls
  // back to.
  await scene.setPair(viewerHull.value, viewerTurret.value, subject.value, null);
  loaded.value = true;
});

/**
 * The item can change under an open workbench — the grid is live, and a purchase
 * re-reads the garage — so the viewer follows rather than being built once.
 *
 * **Watched as a joined string, and that is not a style choice.** The obvious
 * form here is a getter returning `[hull, turret, subject]`, and it is a trap:
 * Vue compares a getter's result with `Object.is`, a fresh array is never equal
 * to the previous one, so the watcher fires on *every* reactive re-render rather
 * than when the pair actually changes. Each firing tore down the `TankVisual`
 * and bumped the generation, which aborted the GLB the previous one had in
 * flight — so the viewer drew the procedural belts, which this class builds
 * itself, with **no hull mesh at all**. A Dart came up as two bare tracks.
 *
 * The multi-source form Vue does support is an array *of getters*
 * (`[() => a, () => b]`); a scalar key is simpler and cannot be written wrongly
 * by accident.
 */
watch(
  () => `${viewerHull.value}|${viewerTurret.value}|${subject.value}`,
  async () => {
    if (!scene) return;
    loaded.value = false;
    await scene.setPair(viewerHull.value, viewerTurret.value, subject.value, null);
    loaded.value = true;
  },
);

onBeforeUnmount(() => {
  // Disposed on close rather than cached for the next open: the garage's context
  // budget allows three, and with this open they are all three spoken for. See
  // the scene's own header.
  scene?.dispose();
  scene = null;
});

let dragging = false;
let lastX = 0;
let lastY = 0;

function onPointerDown(event: PointerEvent): void {
  if (!scene) return;
  dragging = true;
  lastX = event.clientX;
  lastY = event.clientY;
  // Pointer capture rather than window listeners: a drag that leaves the canvas
  // must keep turning the model, and a `pointerup` delivered outside an element
  // that has not captured is simply lost — which is how the research tree's pan
  // once stuck in a drag for ever.
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
}

function onPointerMove(event: PointerEvent): void {
  if (!dragging || !scene) return;
  scene.orbit(event.clientX - lastX, event.clientY - lastY);
  lastX = event.clientX;
  lastY = event.clientY;
  turned.value = true;
}

function onPointerUp(event: PointerEvent): void {
  dragging = false;
  (event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId);
}

function resetView(): void {
  scene?.resetView();
  zoomed.value = false;
}

function toggleZoom(): void {
  if (!scene) return;
  zoomed.value = scene.cycleZoom() !== 0;
}
</script>

<template>
  <div class="scrim" data-test="workbench" @click.self="emit('close')">
    <section
      ref="dialog"
      class="panel"
      role="dialog"
      aria-modal="true"
      :aria-label="`${item.name} — ${kindLabel}`"
      :data-workbench="item.id"
      tabindex="-1"
      @keydown.esc="emit('close')"
    >
      <div class="title-bar">
        <h2 data-test="workbench-name">{{ item.name }}</h2>
        <p class="kind">Tier {{ item.tier }} · {{ kindLabel }}</p>
        <p v-if="item.equipped" class="fitted-label" data-test="workbench-fitted">Fitted</p>
      </div>

      <button
        type="button"
        class="close"
        data-test="workbench-close"
        aria-label="Close"
        @click="emit('close')"
      >
        ✕
      </button>

      <div class="body">
        <div class="viewer" data-test="workbench-viewer">
          <canvas
            v-if="!modelsOff"
            ref="canvas"
            class="stage"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerUp"
          ></canvas>

          <p v-if="modelsOff" class="viewer-note" data-test="workbench-previews-off">
            Model previews are switched off in settings.
          </p>
          <p v-else-if="!loaded" class="viewer-note">Loading model…</p>

          <div v-if="!modelsOff" class="viewer-tools">
            <button
              type="button"
              data-test="workbench-reset"
              title="Reset the view"
              aria-label="Reset the view"
              @click="resetView"
            >
              ↺
            </button>
            <button
              type="button"
              :class="{ on: zoomed }"
              :aria-pressed="zoomed"
              data-test="workbench-zoom"
              title="Zoom"
              aria-label="Zoom"
              @click="toggleZoom"
            >
              ⌖
            </button>
          </div>

          <p v-if="!modelsOff && !turned" class="hint">Drag to rotate</p>
        </div>

        <div class="detail">
          <p class="blurb">{{ item.blurb }}</p>

          <StatList :descriptors="descriptors" :values="item.currentStats" :against="reference" />

          <p v-if="researchLocked" class="gate">
            Requires
            <button
              v-if="gateNodeId"
              type="button"
              class="gate-link"
              :data-action="`workbench-gate-${item.id}`"
              @click="emit('showResearch', gateNodeId)"
            >
              {{ gateLabel }}
            </button>
            <strong v-else>{{ gateLabel }}</strong>
          </p>

          <p v-if="rankLocked" class="gate" :data-action="`workbench-rank-${item.id}`">
            Issued at rank {{ item.requiredRank }} — <strong>{{ item.requiredRankTitle }}</strong>
          </p>

          <p
            v-if="awardedAt !== null && !item.owned"
            class="gate award"
            :data-action="`workbench-award-${item.id}`"
          >
            Awarded at rank {{ awardedAt }} — <strong>{{ item.awardedAtRankTitle }}</strong>.
            Not for sale.
          </p>

          <p v-else-if="item.retired && item.owned" class="gate">
            Withdrawn from the shop. Yours to keep, and it still fits.
          </p>

          <div class="controls">
            <div v-if="item.owned && (item.maxUpgradeLevel > 0 || showsPrestige)" class="ladders">
              <div v-if="item.maxUpgradeLevel > 0" class="ladder">
                <span class="ladder-label">Upgrade {{ item.level }} / {{ item.maxUpgradeLevel }}</span>
                <span class="pips">
                  <i v-for="n in item.maxUpgradeLevel" :key="n" :class="{ filled: n <= item.level }"></i>
                </span>
              </div>

              <div v-if="showsPrestige" class="ladder top-tier" :class="{ locked: !item.prestigeUnlocked }">
                <span class="ladder-label">Top tier {{ item.prestige }} / {{ item.maxPrestigeLevel }}</span>
                <span class="pips">
                  <i
                    v-for="n in item.maxPrestigeLevel"
                    :key="n"
                    :class="{ filled: n <= item.prestige }"
                  ></i>
                </span>
              </div>
            </div>

            <div class="actions">
              <button
                v-if="!item.owned && forSale"
                type="button"
                class="primary"
                :class="{ gem: isPremium }"
                :data-action="`workbench-buy-${item.id}`"
                :disabled="!canBuy || busy"
                @click="emit('purchase', item.id)"
              >
                <template v-if="locked">Locked</template>
                <template v-else-if="!item.affordable">
                  {{ isPremium ? 'Not enough diamonds' : 'Not enough credits' }}
                </template>
                <template v-else>Buy</template>
                · {{ priceAmount }} <CurrencyIcon :kind="priceKind" />
              </button>

              <template v-else-if="item.owned">
                <!--
                  Owned and equipped is the orange "Fitted" label in the title
                  bar now — see the note there — so this button is only drawn
                  for the state it can still change something in: owned but
                  not the tank's current fit. That is what turns the row into
                  the two buttons the fitted state is meant to leave behind,
                  Upgrade and Customize, without a disabled "Fitted" sitting
                  between them saying the same thing the label already said.
                -->
                <button
                  v-if="!item.equipped"
                  type="button"
                  class="primary"
                  :data-action="`workbench-equip-${item.id}`"
                  :disabled="busy"
                  @click="emit('equip', item.id)"
                >
                  Fit
                </button>

                <UpgradeInstallButton
                  v-if="item.upgrade"
                  :item-id="item.id"
                  :install="item.upgrade"
                  :diamonds="diamonds"
                  :busy="busy"
                  action-prefix="workbench-"
                  @install="(id) => emit('installUpgrade', id)"
                  @skip="(id) => emit('skipUpgrade', id)"
                />

                <button
                  v-else-if="item.maxUpgradeLevel > 0 && !maxed"
                  type="button"
                  :data-action="`workbench-upgrade-${item.id}`"
                  :disabled="!canUpgrade || busy"
                  @click="emit('upgrade', item.id)"
                >
                  Upgrade · {{ formatCredits(item.nextUpgradeCost ?? 0) }}
                  <CurrencyIcon kind="credits" />
                  <small v-if="upgradeGain">{{ upgradeGain }}</small>
                </button>

                <button
                  v-else-if="showsPrestige"
                  type="button"
                  class="gem"
                  :data-action="`workbench-prestige-${item.id}`"
                  :disabled="!canPrestige || busy"
                  @click="emit('prestige', item.id)"
                >
                  <template v-if="prestigeMaxed">Top tier reached</template>
                  <template v-else-if="!item.prestigeUnlocked">Fully upgraded</template>
                  <template v-else>
                    Top tier · {{ item.nextPrestigeCost }} <CurrencyIcon kind="diamonds" />
                  </template>
                </button>
              </template>

              <button
                v-if="isHull"
                type="button"
                :data-action="`workbench-customise-${item.id}`"
                :disabled="!canCustomise || busy"
                :title="canCustomise ? undefined : 'Buy this hull to fit parts to it'"
                @click="emit('customise', item.id)"
              >
                Customize
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.scrim {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: grid;
  place-items: center;
  padding: 1.5rem;
  background: rgb(4 6 9 / 78%);
  backdrop-filter: blur(3px);
}

.panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(64rem, 100%);
  max-height: 100%;
  overflow: hidden;
  color: var(--steel-100, #e6ebf0);
  background: var(--steel-900, #10151b);
  border: 1px solid var(--steel-700, #2a323c);
  /* Sharper than the 0.75rem this had — the rest of the garage's chrome
     (`ItemCard`'s own cards, the title bar's buttons, `.ghost`) sits at
     2–4px, and a modal rounder than everything it was opened from reads as a
     different product. */
  border-radius: 0.4rem;
  box-shadow: 0 1.5rem 4rem rgb(0 0 0 / 55%);
}

/*
 * The name and the tier, over the top of the model and the stats rather than
 * in a bar above them — the header this replaced took a full row for two lines
 * of text, and the room is worth more spent on the model beneath it. `absolute`
 * against `.panel`, so it reads as a caption across the whole dialog rather than
 * as a heading for whichever column happens to be under it.
 *
 * `pointer-events: none` on purpose: dragging the model starts wherever the
 * pointer goes down, and this bar's own top few pixels overlap the canvas. A
 * bar that swallowed that first pointerdown would make the top edge of the
 * viewer un-draggable for no reason a player could see.
 */
.title-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  padding: 0.7rem 3.4rem 0.6rem 1.1rem;
  /*
   * Shorter and crisper than the first pass: the fade used to run three
   * quarters of the bar's own height before giving up the last quarter to
   * transparent, which read as a soft wash rather than a plate the name sits
   * on. The dark stop now holds through most of the text's own height and
   * lets go over a narrower band, so the edge under the title reads as an
   * edge rather than a gradient.
   */
  background: linear-gradient(
    to bottom,
    rgb(6 8 11 / 90%) 0%,
    rgb(6 8 11 / 78%) 55%,
    transparent 100%
  );
  pointer-events: none;
}

h2 {
  margin: 0;
  font-size: 1.35rem;
  line-height: 1.1;
  color: #fff;
}

.kind {
  margin: 0.2rem 0 0;
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  /* Brighter than the panel's usual muted label — this sits over a photograph
     of the model, not over the panel's own flat background, and `--steel-400`
     read as barely-there against a bright hull. */
  color: var(--steel-200, #c7d2dc);
}

/*
 * What a disabled "Fitted" button used to say, said once, here — freeing the
 * action row below to the two buttons that still do something: Upgrade and
 * Customize. The accent colour is the same "this is fitted" cue `ItemCard`'s
 * own badge and border already use, so the word means the same thing on the
 * card this was opened from and here.
 */
.fitted-label {
  margin: 0.3rem 0 0;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--accent, #e8722c);
}

/*
 * Floating in the panel's own corner, over whatever the viewer is showing —
 * the reason it carries a background now where the old header's close button
 * did not need one: that button sat on a solid bar, this one sits on a render.
 * Same dark translucent chip the viewer's own tools use, so every floating
 * control in this dialog reads as one family.
 */
.close {
  position: absolute;
  top: 0.7rem;
  right: 0.7rem;
  z-index: 3;
  display: grid;
  place-items: center;
  width: 2.2rem;
  height: 2.2rem;
  color: var(--steel-200, #c7d2dc);
  background: rgb(10 14 18 / 72%);
  backdrop-filter: blur(4px);
  border: 1px solid var(--steel-700, #2a323c);
  /* A rounded square, matching `.viewer-tools button` below it rather than a
     circle — the same "less round" pass the panel itself took. */
  border-radius: 0.35rem;
  cursor: pointer;
}

.close:hover {
  color: var(--steel-100, #e6ebf0);
  border-color: var(--steel-500, #6d7987);
}

.body {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  min-height: 0;
  overflow: hidden;
}

.viewer {
  position: relative;
  /* Taller than it was: with the header and footer both gone, the panel has
     the room to spend on the one thing it exists to show. */
  min-height: 22rem;
  background:
    repeating-linear-gradient(
      135deg,
      rgb(255 255 255 / 2%) 0 1px,
      transparent 1px 9px
    ),
    var(--steel-850, #151b22);
  border-right: 1px solid var(--steel-700, #2a323c);
}

.stage {
  display: block;
  width: 100%;
  height: 100%;
  /* The canvas is driven by pointer only, and a drag on it must not select the
     text in the panel beside it. */
  touch-action: none;
  cursor: grab;
}

.stage:active {
  cursor: grabbing;
}

.viewer-note {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  margin: 0;
  font-size: 0.8rem;
  color: var(--steel-400, #8b97a5);
  pointer-events: none;
}

.viewer-tools {
  position: absolute;
  bottom: 0.7rem;
  left: 0.7rem;
  display: flex;
  gap: 0.4rem;
}

.viewer-tools button {
  width: 2.1rem;
  height: 2.1rem;
  font-size: 0.95rem;
  color: var(--steel-300, #aab5c1);
  background: rgb(10 14 18 / 70%);
  border: 1px solid var(--steel-700, #2a323c);
  border-radius: 0.35rem;
  cursor: pointer;
}

.viewer-tools button:hover {
  color: var(--steel-100, #e6ebf0);
  border-color: var(--steel-500, #6d7987);
}

.viewer-tools button.on {
  color: var(--accent, #e8722c);
  border-color: var(--accent, #e8722c);
}

.hint {
  position: absolute;
  right: 0.9rem;
  bottom: 1rem;
  margin: 0;
  font-size: 0.62rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel-500, #6d7987);
  pointer-events: none;
}

/*
 * A flex column, not a plain block — `.controls` below needs `margin-top: auto`
 * to sit at the foot of whatever the blurb, stats and gates leave above it, and
 * that only works inside a flex container. Every child still lays out top to
 * bottom exactly as it did in a plain block context.
 */
.detail {
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* The title bar overlays this column's own top edge, so its first real line
     of content needs clearance the desktop header used to provide for free. */
  padding: 3.1rem 1.1rem 1rem;
  overflow-y: auto;
}

.blurb {
  margin: 0 0 0.9rem;
  font-size: 0.9rem;
  line-height: 1.45;
  color: var(--steel-200, #c7d2dc);
}

/* More contrast than `StatList`'s own default: that component is also the
   shop card's readout, tuned for a label under a bright card in a lit grid,
   and the same tone read as barely-there on this darker panel. */
.detail :deep(dt) {
  color: var(--steel-200, #c7d2dc);
}

/* Bottom margin too, not only top — a gate line is often the last thing
   above `.controls`' own border-top, and with none of its own it sat flush
   against that rule with nothing to separate the two. */
.gate {
  margin: 0.8rem 0 0.6rem;
  font-size: 0.78rem;
  color: var(--steel-200, #c7d2dc);
}

.gate.award {
  color: #d9b25f;
}

.gate-link {
  padding: 0;
  font: inherit;
  color: var(--accent, #e8722c);
  text-decoration: underline;
  background: none;
  border: 0;
  cursor: pointer;
}

/*
 * The footer this replaced sat below both columns, full width; this sits at
 * the foot of the detail column alone, under the stats it belongs beside —
 * which is what let the viewer beside it grow into the room the footer used
 * to occupy.
 */
.controls {
  margin-top: auto;
  padding-top: 0.85rem;
  border-top: 1px solid var(--steel-700, #2a323c);
}

.ladders {
  display: flex;
  gap: 1.5rem;
  justify-content: space-between;
  margin-bottom: 0.7rem;
}

.ladder {
  flex: 1;
  min-width: 0;
}

.ladder.top-tier {
  flex: 0 1 auto;
  text-align: right;
}

.ladder-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.62rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--steel-400, #8b97a5);
}

.pips {
  display: flex;
  gap: 0.25rem;
}

.ladder.top-tier .pips {
  justify-content: flex-end;
}

.pips i {
  flex: 1;
  min-width: 0.8rem;
  height: 0.3rem;
  background: var(--steel-700, #2a323c);
  border-radius: 0.15rem;
}

.pips i.filled {
  background: var(--accent, #e8722c);
}

.ladder.top-tier .pips i {
  flex: none;
  width: 1.6rem;
}

.ladder.top-tier .pips i.filled {
  background: #8fd6ff;
}

/*
 * Smaller than the old full-width footer buttons — they sit in a column now,
 * not across the whole dialog, and a slab sized for that width read oversized
 * in this one.
 */
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.actions button {
  flex: 1;
  min-width: 7rem;
  padding: 0.55rem 0.7rem;
  font: inherit;
  /* A figure on nearly every one of these — a price, a level, a delta — so
     the data face rather than the body one, the same reasoning the credits
     chip and the stat list already read their own numbers in. */
  font-family: var(--font-mono, ui-monospace, 'SF Mono', monospace);
  font-size: 0.8rem;
  color: var(--steel-100, #e6ebf0);
  background: var(--steel-800, #1a212a);
  border: 1px solid var(--steel-700, #2a323c);
  border-radius: 0.4rem;
  cursor: pointer;
}

.actions button small {
  margin-left: 0.35rem;
  font-size: 0.78em;
  color: #9fe07a;
}

.actions button:hover:not(:disabled) {
  border-color: var(--steel-500, #6d7987);
}

.actions button.primary {
  flex: 2;
  font-weight: 600;
  color: #14100b;
  background: var(--accent, #e8722c);
  border-color: transparent;
}

.actions button.gem {
  color: #06202e;
  background: #8fd6ff;
  border-color: transparent;
}

.actions button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

@media (width <= 52rem) {
  /* The two columns stop fitting long before the text does — the viewer is
     squeezed to a letterbox first — so it stacks, model above detail, which is
     the reading order the title bar already sets up. */
  .body {
    grid-template-columns: minmax(0, 1fr);
    overflow-y: auto;
  }

  .viewer {
    min-height: 15rem;
    border-right: 0;
    border-bottom: 1px solid var(--steel-700, #2a323c);
  }
}

/*
 * ─── A phone held sideways ───────────────────────────────────────────────────
 *
 * Every rule below is gated on `.garage.compact` — the class `GarageScreen`
 * writes from its single `matchMedia(COMPACT_QUERY)`. This panel is a DOM
 * descendant of that element (the scrim is `position: fixed`, which moves where
 * it *paints* and not where it sits in the tree), so an ancestor selector
 * reaches it, and Vue's scoped styles attach their attribute to the last
 * compound selector only — `.garage.compact .panel` compiles to
 * `.garage.compact .panel[data-v-…]` and needs nothing of the ancestor.
 *
 * It was a `@media (height <= 520px)` block first, which worked and was still
 * wrong: 520 is `COMPACT_QUERY`'s own number, and restating it here made the
 * breakpoint two facts that agree today. This project's rule is the one applied
 * to `--bar-h` and to `WaveMission.homePoint()` — derive it, do not restate it —
 * so the panel now switches on exactly the same condition the screen behind it
 * switches on, and retuning 520 moves both.
 *
 * It must still come *after* the `52rem` width block: both match a landscape
 * phone, they collide on `.body`, and with equal-weight selectors source order
 * is what decides which one a 667×375 viewport gets. (These are heavier — two
 * classes and a descendant against one class — so order is belt to specificity's
 * braces, but the block reads as an override and should be positioned as one.)
 *
 * ── Why the width query is wrong here, on its own ─────────────────────────
 *
 * `width <= 52rem` stacks the panel — model above detail — and that is right for
 * a narrow window, where the two columns would squeeze the viewer into a
 * letterbox. A landscape phone is not a narrow window: it is 667 px wide and
 * 375 px *tall*, so it matches that query on width while having none of the
 * problem the query exists to solve. So on a short viewport the two columns
 * come back, and it is the *viewer's* height floor that has to go rather than
 * the column beside it — a fixed minimum on a 300 px panel is a floor taller
 * than the room.
 */

/* Nearly the whole screen: 1.5 rem of scrim on a 375 px viewport is 26% of the
   height spent on a border. */
.garage.compact .scrim {
  padding: 0.5rem;
}

.garage.compact .panel {
  border-radius: 0.5rem;
}

/* Right padding clears the compact close button's own footprint (0.7rem
   offset + its 44px/2.75rem side, so its near edge sits ~3.45rem in) with a
   little margin, the same relationship the desktop padding holds to its own
   smaller button. */
.garage.compact .title-bar {
  padding: 0.5rem 4rem 0.7rem 0.7rem;
}

.garage.compact h2 {
  font-size: 1.05rem;
}

/* Two columns again — the model beside the detail, not above it. */
.garage.compact .body {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
  overflow: hidden;
}

/*
 * The floor goes entirely. `.body` is a grid row of the panel's flex column
 * with `min-height: 0`, so the viewer takes the height the row is given and
 * the canvas fills it — which is the whole point of keeping the columns: the
 * model gets every pixel of the short edge instead of a fixed minimum of it.
 *
 * The detail column is slightly the wider of the two, reversing the standing
 * ratio. On a desktop the viewer leads because the model is what the window is
 * for; here the column that has to hold a blurb, six stat rows, two ladders
 * and up to three buttons is the one that cannot be compressed, and an
 * orbitable tank reads perfectly well in 300×300.
 */
.garage.compact .viewer {
  min-height: 0;
  border-right: 1px solid var(--steel-700, #2a323c);
  border-bottom: 0;
}

/*
 * `3.6rem` of top padding, not `2.5rem` — the title bar's own height is not
 * what this has to clear here. The close button is `top: 0.7rem` plus its
 * compact `44px` height, so its bottom edge sits at 0.7rem + 44px ≈ 3.45rem;
 * measured, `2.5rem` put the blurb's first line squarely inside that circle
 * and "remarkable and" rendered as "remarkabl[…]d". Desktop never had this —
 * its own close button is smaller and `.detail`'s 3.1rem already cleared it —
 * so the fault was compact-only and compact needed its own number.
 */
.garage.compact .detail {
  padding: 3.6rem 0.7rem 0.6rem;
}

/* 0.45 rem rather than 0.6: with the stat block folded the pane came to 181 px
   of content in a 180 px box, and one pixel of overflow is a scrollbar and a
   clipped comparison track. */
.garage.compact .blurb {
  margin: 0 0 0.45rem;
  font-size: 0.8rem;
}

/*
 * The same fold the card's stat block takes, for the same reason and measured
 * the same way: six rows in one column is 250 px of content in a 180 px
 * detail pane, so `HULL TRAVERSE` and `REVERSE` were below the fold of a
 * window somebody opened *to compare stats*. Two columns of stacked cells is
 * ~93 px and the pane stops scrolling at all.
 *
 * Two rather than the card's three, because this column is 347 px against the
 * card's 447 and it also carries the blurb. Only the list's own
 * `grid-template-columns` moves — the rows are already grid items.
 */
.garage.compact .detail :deep(.stats) {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.4rem 0.7rem;
}

.garage.compact .detail :deep(.stats .row) {
  grid-template-columns: minmax(0, 1fr);
  gap: 0.1rem;
}

.garage.compact .detail :deep(.stats dt) {
  font-size: 0.6rem;
  letter-spacing: 0.06em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.garage.compact .detail :deep(.stats dd) {
  font-size: 0.75rem;
  gap: 0.3rem;
}

/* Every control in the panel, against the 44 px floor. The close button
   measured 32×32 and the viewer's tools 34×34 — and the tools sit over a
   canvas that swallows a drag, so a near-miss on one of them rotates the model
   instead of doing nothing, which is the worse failure. */
.garage.compact .close {
  width: 44px;
  height: 44px;
}

.garage.compact .viewer-tools button {
  width: 44px;
  height: 44px;
}

.garage.compact .actions button {
  min-height: 44px;
  padding: 0.5rem 0.6rem;
  min-width: 6rem;
  font-size: 0.78rem;
}

/*
 * The upgrade button's gain — `+0.7 m/s` — is an inline `<small>` after a
 * currency mark, and at this font size the line runs out inside it: what a
 * player actually read was `+0.7` on one line and `m/s` on the next, which is a
 * unit torn in half rather than a two-line button. Nowrap keeps the figure and
 * its unit together and lets the break fall in front of them, where a break in
 * a `Upgrade · 260 🪙 · +0.7 m/s` label belongs. Not `nowrap` on the *button*:
 * that would push the whole label past the 6 rem minimum and out of the panel.
 */
.garage.compact .actions button small {
  white-space: nowrap;
}

/* The same sentence-with-a-button-in-it as the card's gate line, and the same
   fix: 44 px as an inline box, so the line still reads as a line. */
.garage.compact .gate-link {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
}
</style>
